"use client";
import AvatarGroup from "@/components/c-avatar-group";
import Header from "@/components/c-header";
import CheckmarkIcon from "@/components/icons/c-checkmark-icon";
import CustomHorizontalListItem from "@/components/list/c-custom-horizontal-list-item";
import { Page } from "@/components/ui/page";
import React, { useEffect } from "react";
import LewkboardForm from "@/components/form/f-lewkboard";
import ProductShowcaseCard from "@/components/card/c-product-showcase-card";
import useMeasure from "react-use-measure";
import { animate, useMotionValue, motion as m } from "framer-motion";
import useAuth, { createClient } from "@/lib/utils/supabase/client";

const IntroPage = () => {
  // --------------------------------- CONST --------------------------------- //


  const AVATAR_IMAGE_PATH_LIST = [
    "/assets/avatars/user-1.png",
    "/assets/avatars/user-2.png",
    "/assets/avatars/user-3.png",
    "/assets/avatars/user-4.png",
    "/assets/avatars/user-5.png",
  ];

  // --------------------------------- HOOKS --------------------------------- //

  let [ref, { width }] = useMeasure();
  const xTranslationRight = useMotionValue(0);
  const xTranslationLeft = useMotionValue(0);

  const auth = useAuth();
  // --------------------------------- FUNCTIONS --------------------------------- //
  const getOutfits = (
    itemType: "accessory" | "bottom" | "shoe" | "top"
  ): string[] => {
    let outfitItemsArray = [];
    for (let i = 1; i <= 20; i++) {
      outfitItemsArray.push(`/assets/outfits/outfit-${i}/${itemType}.png`);
    }
    return outfitItemsArray;
  };

  // --------------------------------- USE EFFECT --------------------------------- //
  useEffect(() => {
    let controlsRight;
    let controlsLeft;
    let finalPos = -width / 2 - 10;

    controlsRight = animate(xTranslationRight, [0, finalPos], {
      ease: "linear",
      duration: 100,
      repeat: Infinity,
      repeatType: "loop",
      repeatDelay: 0,
    });

    controlsLeft = animate(xTranslationLeft, [finalPos, 0], {
      ease: "linear",
      duration: 100,
      repeat: Infinity,
      repeatType: "loop",
      repeatDelay: 0,
    });

    return () => {
      controlsRight.stop();
      controlsLeft.stop();
    };
  }, [xTranslationRight, xTranslationLeft, width]);

  

  return (
    <Page
      className=" relative overflow-x-hidden px-4 md:py-0 pb-4  "
      size={"full"}
    >
      <Header {...auth} className=" max-w-screen-base mx-auto " />

      <div className=" absolute top-0 left-0 w-full md:max-w-[56rem] max-w-[18rem] h-96 bg-white blur-[50px] rounded-br-[20rem] -z-10" />
      <div className=" flex flex-col absolute overflow-hidden top-[4.1rem] md:top-[14.5rem] gap-y-[1.25rem] -z-20 w-full left-1/2 pt-[1.25rem] -translate-x-1/2 max-w-[1680px] ">
        <div className=" absolute left-0 h-full w-30 bg-gradient-to-r from-white to-transparent z-10" />
        <div className=" absolute right-0 h-full w-30 bg-gradient-to-l from-white to-transparent z-10" />
        <div className=" absolute bottom-0 w-full h-30 bg-gradient-to-t from-white to-transparent z-10" />
        <m.div
          style={{ x: xTranslationRight }}
          ref={ref}
          className=" flex gap-[1.25rem] w-max will-change-transform"
        >
          {[...getOutfits("accessory"), ...getOutfits("accessory")].map(
            (image, index) => (
              <ProductShowcaseCard key={index} imagePath={image} />
            )
          )}
        </m.div>
        <m.div
          style={{ x: xTranslationLeft }}
          ref={ref}
          className=" flex gap-[1.25rem] w-max will-change-transform"
        >
          {[...getOutfits("top"), ...getOutfits("top")].map((image, index) => (
            <ProductShowcaseCard key={index} imagePath={image} />
          ))}
        </m.div>
        <m.div
          style={{ x: xTranslationRight }}
          ref={ref}
          className=" flex gap-[1.25rem] w-max will-change-transform"
        >
          {[...getOutfits("bottom"), ...getOutfits("bottom")].map(
            (image, index) => (
              <ProductShowcaseCard key={index} imagePath={image} />
            )
          )}
        </m.div>
        <m.div
          style={{ x: xTranslationLeft }}
          ref={ref}
          className=" flex gap-[1.25rem] w-max will-change-transform"
        >
          {[...getOutfits("shoe"), ...getOutfits("shoe")].map(
            (image, index) => (
              <ProductShowcaseCard key={index} imagePath={image} />
            )
          )}
        </m.div>
      </div>

      {/* Intro Text Container */}
      <div className=" max-w-screen-base mx-auto md:pt-[1.18rem] md:flex-col">
        <h1 className=" font-bai-jamjuree md:text-title font-medium md:leading-sm text-left text-title-mobile leading-md  ">
          Your AI-Powered Fashion Stylist
        </h1>
        <h2 className=" md:text-heading md:leading-sm text-left md:mt-4 mt-5 font-normal text-heading-mobile leading-[1.44] ">
          Discover your perfect outfit in seconds, curated for any occasion.
        </h2>

        <div className=" flex flex-col gap-y-4 md:flex-row md:gap-x-10 md:mt-6 mt-7">
          <CustomHorizontalListItem
            icon={
              <CheckmarkIcon className=" bg-lewk-primary rounded-full size-5" />
            }
            text="Discover New Brands"
          />
          <CustomHorizontalListItem
            icon={
              <CheckmarkIcon className=" bg-lewk-primary rounded-full size-5" />
            }
            text="Shop Complete Outfits"
          />
          <CustomHorizontalListItem
            icon={
              <CheckmarkIcon className=" bg-lewk-primary rounded-full size-5" />
            }
            text="24/7 Virtual Stylist"
          />
        </div>

        <CustomHorizontalListItem
          className=" gap-3 leading-sm md:mt-6 font-normal text-left mt-7 "
          icon={<AvatarGroup images={AVATAR_IMAGE_PATH_LIST} />}
          text="Join over 2000+ online shoppers."
        />

        <div className=" max-w-[25rem] h-[30rem] bg-white border border-lewk-grey rounded-base shadow-container mt-8 p-4">
          <LewkboardForm />
        </div>
       
      </div>
    </Page>
  );
};

export default IntroPage;
